import { Alert, Button, Input, Modal, Space } from "antd";
import type { ShippingOwnerCluesVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, Grid as ZhGrid } from "zhihao-ui-react";

import { DATETIME_PICKER_FORMAT } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { SHIPPING_OWNER_CLUES_STATE, TRIGGER_PAGE } from "@zhihaoscm/meta";
import { dateFormat } from "@/utils";

/** 复用旧 Vue 客户 JSON 的账号、手机号和编码拼接规则，忽略缺失字段。 */
function getCaptainLabel(captainEnterprise: ShippingOwnerCluesVo["shippingOwnerClues"]["captainEnterprise"]): string {
    return (
        [
            captainEnterprise?.institutionName || captainEnterprise?.realName,
            captainEnterprise?.mobile,
            captainEnterprise?.code
        ]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

export async function loadShippingOwnerClue(id: number | string) {
    const { shippingOwnerCluesService } = await import("@/api/services/shippingOwnerCluesService");
    return shippingOwnerCluesService.getVo(id);
}
export interface ShippingOwnerCluesDetailPageProps {
    loadClue?: (id: number | string) => Promise<ShippingOwnerCluesVo>;
    updateClue?: (id: number | string, form: { state: number; remark: string }) => Promise<unknown>;
    permissions?: string[];
}

export async function updateShippingOwnerClueDetail(id: number | string, form: { state: number; remark: string }) {
    const { shippingOwnerCluesService } = await import("@/api/services/shippingOwnerCluesService");
    return shippingOwnerCluesService.update(id, form);
}

/** 船主线索详情：复刻 Vue 的基本信息、处理记录和状态权限判断。 */
export function ShippingOwnerCluesDetailPage({
    loadClue = loadShippingOwnerClue,
    updateClue = updateShippingOwnerClueDetail,
    permissions
}: ShippingOwnerCluesDetailPageProps) {
    const { id } = useParams();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const canWrite = (permissions ?? storePermissions).includes("ROLE_SHIP_CLUE_W");
    const [data, setData] = useState<ShippingOwnerCluesVo | null>(null);
    const [error, setError] = useState("");
    const [processing, setProcessing] = useState(false);
    const [remark, setRemark] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!id) return;
        void loadClue(id)
            .then(setData)
            .catch(() => setError("船主线索详情加载失败"));
    }, [id, loadClue]);
    if (error) return <Alert message={error} type="error" />;
    if (!data) return <section aria-busy="true" aria-label="shipping-owner-clues-detail-page" />;
    const clue = data.shippingOwnerClues;
    return (
        <section aria-label="shipping-owner-clues-detail-page">
            <div className="section-card">
                <div className="flex items-center justify-between">
                    <div className="text-lg font-medium">船主线索详情</div>
                    <div>{SHIPPING_OWNER_CLUES_STATE.getIntroByCode(clue.state) || "-"}</div>
                </div>
                <div className="section-title mt-6">基本信息</div>
                <ZhGrid className="mt-5" cols={4} gap={[30, 12]}>
                    <ZhBaseItem label="承运商账号" value={getCaptainLabel(clue.captainEnterprise)} />
                    <ZhBaseItem label="是否绑定船舶" value={data.existShip ? "是" : "否"} />
                    <ZhBaseItem label="船舶">
                        {data.ships?.length
                            ? data.ships.map((ship, index) => (
                                  <span key={ship.id}>
                                      <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${ship.id}`}>
                                          {ship.cnname || ship.name}
                                      </Link>
                                      {index < data.ships.length - 1 ? "、" : null}
                                  </span>
                              ))
                            : "--"}
                    </ZhBaseItem>
                    <ZhBaseItem label="联系方式" value={clue.mobile || "--"} />
                    <ZhBaseItem label="触发页面" value={TRIGGER_PAGE.getIntroByCode(clue.triggerPage) || "--"} />
                    <ZhBaseItem
                        label="触发时间"
                        value={clue.createdTime ? dateFormat(clue.createdTime, DATETIME_PICKER_FORMAT) : "--"}
                    />
                </ZhGrid>
                {clue.modifyRecord?.length ? (
                    <>
                        <div className="section-title mt-6">处理记录</div>
                        <div className="mt-5 flex flex-col gap-4">
                            {clue.modifyRecord.map((record, index) => (
                                <div key={`${record.updatedTime}-${index}`}>
                                    <div>
                                        {index + 1}. {dateFormat(record.updatedTime, DATETIME_PICKER_FORMAT)}，处理人：
                                        {record.handlerName}
                                    </div>
                                    <div>备注：{record.remark}</div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : null}
                <Space className="mt-4">
                    {canWrite && Number(clue.state) !== SHIPPING_OWNER_CLUES_STATE.PROCESSED.code ? (
                        <Button disabled={banButton} type="primary" onClick={() => setProcessing(true)}>
                            处理
                        </Button>
                    ) : null}
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
                <Modal
                    open={processing}
                    title="处理船主线索"
                    okText="确认处理"
                    cancelText="取消"
                    confirmLoading={saving}
                    onOk={async () => {
                        if (saving || !id) return;
                        setSaving(true);
                        try {
                            await updateClue(id, { state: SHIPPING_OWNER_CLUES_STATE.PROCESSED.code, remark });
                            // 处理成功后重新拉取详情，回显服务端状态和最新处理记录。
                            const refreshedData = await loadClue(id);
                            setData(refreshedData);
                            setProcessing(false);
                        } catch {
                            setError("线索处理失败");
                        } finally {
                            setSaving(false);
                        }
                    }}
                    onCancel={() => {
                        if (!saving) setProcessing(false);
                    }}
                >
                    <Input.TextArea
                        aria-label="处理备注"
                        value={remark}
                        onChange={event => setRemark(event.target.value)}
                    />
                </Modal>
            </div>
        </section>
    );
}

export default ShippingOwnerCluesDetailPage;
